/**
 * Registro de los documentos legales que la app muestra y registra.
 *
 * Un `doc_type` es la LLAVE del documento en compliance_document_service, no un
 * título: el texto, la versión vigente y la URL del artefacto viven en la base y
 * se piden con `useLegalDocument`. Aquí no hay versiones hardcodeadas a
 * propósito — la versión que se registra es la que el servidor sirvió, así que
 * publicar una versión nueva no necesita un deploy y es IMPOSIBLE que la
 * pantalla muestre un texto y el log firme otro.
 *
 * Agregar un documento = agregar una entrada aquí (y su fila en la base).
 */
/** doc_type del documento que acepta el zubalero. Hoy es `T&Cs Zubale` (doc 5,
 * el contrato de comisión mercantil de 2024). Legal publica el "Anexo del
 * Programa de Cumplimiento Fiscal vía Aliado" como documento NUEVO (decisión
 * Stiven 2026-09-10, t-057), no como versión del 5, así que el cambio de
 * documento es un cambio de LLAVE: se resuelve por env para que el switch
 * viaje con el alta de la fila y no con un deploy de código. Mientras la env no
 * exista, la llave sigue siendo la de hoy y el flujo no se rompe (el gate es
 * fail-closed: una llave sin fila en la base bloquea el avance).
 * TODO(t-057, 2026-09-10): fijar aquí la llave definitiva y retirar la env
 * cuando el anexo esté publicado y el padrón re-aceptado. */
const ZUBALE_LEGAL_DOC_TYPE: string =
	import.meta.env.VITE_ZUBALE_LEGAL_DOC_TYPE || 'T&Cs Zubale';

export const LEGAL_DOCS = {
	terms: 'T&Cs',
	zubale: ZUBALE_LEGAL_DOC_TYPE,
	// Anexo del programa de activación fiscal DiDi (ASAT). El documento se da de
	// alta en compliance_document_service en la fase de ops de t-056; mientras no
	// exista la fila, el gate de aceptación BLOQUEA el avance (fail-closed, igual
	// que Zubale) — nunca se firma contra un documento que no está en la base.
	didi: 'T&Cs DiDi',
	referralsHeruCash: 'T&Cs Referidos y heru-cash',
	/** Casilla opcional de comunicaciones comerciales (t-257): la firma solo
	 * quien la marca, en el alta o en el re-consentimiento. NO entra a
	 * `SIGNUP_LEGAL_DOCS`, que es la lista de lo obligatorio. */
	commercialConsent: 'Consentimiento comercial',
} as const;

export type LegalDocKey = keyof typeof LEGAL_DOCS;
export type LegalDocType = (typeof LEGAL_DOCS)[LegalDocKey];

/** Los documentos que se aceptan en el ALTA, con UN SOLO check. Es data:
 * agregar uno al alta es sumarlo aquí — ni la pantalla ni el registro de la
 * aceptación se tocan.
 *
 * Referidos y heru-cash entra aquí por decisión de Rafael (2026-08-21). Antes
 * vivía en un gate dentro del modal de referidos, con este argumento: su
 * cláusula 3 excluye a colaboradores de heru y a sus familiares, así que pedirlo
 * en el alta pone a firmar las reglas de un programa a gente que nunca va a
 * entrar en él. El argumento sigue siendo cierto y se aceptó el costo: firmar
 * de más no crea elegibilidad —la cláusula 3 sigue excluyendo a quien excluye—
 * mientras que pedirlo después dejaba sin firma a TODO usuario anterior al
 * gate y ponía una fricción justo donde el usuario ya venía a compartir.
 *
 * Consecuencia que hay que tener presente: quien se registró ANTES de este
 * deploy no tiene la firma y ya no hay superficie que se la pida. */
export const SIGNUP_LEGAL_DOCS: readonly LegalDocType[] = [
	LEGAL_DOCS.terms,
	LEGAL_DOCS.referralsHeruCash,
];

/**
 * Desde cuándo una firma cuenta como vigente para el re-consentimiento, por
 * documento: el `created_at` (UTC) de la última versión que legal decidió que
 * TODOS deben volver a aceptar. Quien aceptó cualquier versión desde ese
 * instante está al día aunque después se publique otra; el modal de
 * «Actualizamos nuestros Términos» solo sale a quien no.
 *
 * Es la regla de Stiven (2026-10-19, t-298): publicar una versión no pide
 * re-aceptación por sí solo. Para pedirla, se mueve aquí la fecha a la de la
 * versión nueva (un PR). El alta sigue mostrando y firmando la versión que el
 * servidor sirva, y la casilla del heru-cash del cambio de plan firma esa misma.
 * Un documento sin fecha aquí pide siempre la versión vigente.
 */
export const RECONSENT_REQUIRED_SINCE: Partial<Record<LegalDocType, string>> = {
	[LEGAL_DOCS.terms]: '2026-10-12T00:59:33Z', // 3.2
	[LEGAL_DOCS.referralsHeruCash]: '2026-08-20T21:57:57Z', // 2026-08-05, la primera
};

/** El documento principal del alta: el que da nombre a la fila. Los demás de
 * `SIGNUP_LEGAL_DOCS` se firman con el mismo check. */
export const SIGNUP_TERMS_DOC_TYPE: LegalDocType = LEGAL_DOCS.terms;

/** Copy del error, por documento. El usuario no tiene por qué saber qué es
 * "compliance_log": se le dice qué no se pudo guardar y que puede reintentar. */
export const LEGAL_DOC_LABEL: Record<string, string> = {
	[LEGAL_DOCS.terms]: 'tu aceptación de los Términos y Condiciones',
	[LEGAL_DOCS.zubale]:
		'tu aceptación de los Términos y Condiciones de Zubale',
	[LEGAL_DOCS.didi]:
		'tu aceptación de los Términos del Programa de Activación Fiscal con DiDi',
	[LEGAL_DOCS.referralsHeruCash]:
		'tu aceptación de los Términos y Condiciones del programa de referidos',
	[LEGAL_DOCS.commercialConsent]: 'tu autorización de comunicaciones comerciales',
};

/** Título corto para el link y el encabezado del lector. El nombre largo del
 * documento vive en la base (`doc_name`) y se muestra dentro. */
export const LEGAL_DOC_LINK_LABEL: Record<string, string> = {
	[LEGAL_DOCS.terms]: 'Términos y Condiciones',
	[LEGAL_DOCS.zubale]: 'Términos y Condiciones de Zubale',
	[LEGAL_DOCS.didi]: 'Términos del Programa de Activación Fiscal con DiDi',
	[LEGAL_DOCS.referralsHeruCash]: 'Términos del programa de referidos',
};
